
import AnalyticsEvent from "@/types/AnalyticsEvent.interface"
import IcpState from "@/types/IcpState.interface"

interface State {
    apiBase: string
    appDebug: boolean
    appVersion: string
    isOffline: boolean
    privacyPolicy: string
    termsOfService: string
    useAgreement: string
    websocketsConnected: boolean
    icpData: IcpState
}

interface NumberHostingRequest {
    address: string
    number: string
    provider: string
    media_id: number
}

const defaults = () => ({
    apiBase: `${process.env.VUE_APP_URL_API}`,
    appDebug: (process.env.VUE_APP_DEBUG == 'true'), //  Tracks if the API is in debug mode
    appVersion: (process.env.VUE_APP_VERSION), //  A git commit that the client was built on - this gets updated each time the client is compiled
    icpData: {
        companySize: [
            {id: 0, value: 'Just me'},
            {id: 1, value:  '2-10 employees'},
            {id: 2, value: '11-25 employees'},
            {id: 3, value: '26-100 employees'},
            {id: 4, value: '101+ employees'}
        ],
        industry: [
            {id: 0, value: 'Construction, Materials, or Trade Services'},
            {id: 1, value: 'Hospitality and Travel'},
            {id: 2, value: 'HR, Staffing, Recruitment'},
            {id: 3, value: "Non-Profit"},
            {id: 4, value: "Professional Services (Insurance, Legal, Accounting)"},
            {id: 5, value: "Transportation, Logistics or Dispatch"},
            {id: 6, value: "Other"}
        ],
        teamSize: [
            {id: 0, value: 'Just me'},
            {id: 1, value: '1-3 other people'},
            {id: 2, value: '4-10'},
            {id: 3, value: '11-25'},
            {id: 4, value: '26-100'},
            {id: 5, value: '101+'}
        ],
        reach: [
            {id: 0, value: 'Less than 100 contacts'},
            {id: 1, value: '100 - 1,000 contacts each month'},
            {id: 2, value: '1,000 - 10,000 contacts each month'},
            {id: 3, value: '10,000 + contacts each month'}
        ]
    },              //  Tracks the ICP questions from registration
    isOffline: false,           //  Tracks if there is an active internet connection
    privacyPolicy: null,        //  MessageDesk Privacy policy
    termsOfService: null,       //  MessageDesk Terms of Service
    useAgreement: null,         //  MessageDesk Use Agreement
    websocketsConnected: false  //  Tracks the last time we had a successful pusher connection
})

export default {
    state: defaults(),
    mutations: {
        appDebugSet (state: State, bool: boolean): void {
            state.appDebug = bool
        },
        isOfflineSet (state: State, bool: boolean): void {
            state.isOffline = bool
        },
        privacyPolicySet (state: State, str: string): void {
            state.privacyPolicy = str
        },
        termsOfServiceSet (state: State, str: string): void {
            state.termsOfService = str
        },
        useAgreementSet (state: State, str: string): void {
            state.useAgreement = str
        },
        websocketsConnectedSet(state: State, bool: boolean): void {
            state.websocketsConnected = bool
        }
    },
    actions: {
        analyticsGroup: async ({getters}, data: object): Promise<void> => {
            //  Assemble our URL
            const url = `${getters.api}/analytics/group`

            //  Send our request
            return window.axios.post(url, data).then(response => {
                return response.data
            }).catch(() => null)
        },
        analyticsPost: async ({getters}, event: AnalyticsEvent): Promise<void> => {
            const url = `${getters.api}/analytics`
            return window.axios.post(url, event).then(response => {
                getters.appDebug && console.log('ANALYTICS: event sent', event, response.data)
            }).catch(() => null)
        },
        analyticsReportHosting: async ({getters}, payload: NumberHostingRequest): Promise<void> => {
            //  Create our url
            const url = `${getters.api}/analytics/hosting`

            //  Send our request
            return window.axios.post(url, payload).then(response => {
                return response.data
            }).catch(() => null)
        },
        loadTermsOfService: async ({commit, getters}): Promise<void> => {
            //  Define the location of MessageDesk legal documents
            const terms = `https://messagedesk-legal-storage.s3.us-west-2.amazonaws.com/terms_of_service.txt`
            const privacy = `https://messagedesk-legal-storage.s3.us-west-2.amazonaws.com/privacy_policy.txt`
            const use = `https://messagedesk-legal-storage.s3.us-west-2.amazonaws.com/use_agreement.txt`

            //  Get the currently loaded documents
            let pp = getters.privacyPolicy
            let ts = getters.termsOfService
            let ua = getters.useAgreement

            //  If a document does NOT exist, fetch it
            if (!pp) pp = await window.getFileContent(privacy)
            if (!ts) ts = await window.getFileContent(terms)
            if (!ua) ua = await window.getFileContent(use)
    
            //  Set the legal documents
            commit('privacyPolicySet', pp)
            commit('termsOfServiceSet', ts)
            commit('useAgreementSet', ua)
        }
    },
    getters: {
        apiBase (state: State): string {
            return state.apiBase
        },
        appDebug (state: State): boolean {
            return state.appDebug
        },
        appVersion (state: State): string {
            return state.appVersion
        },
        icpData (state: State): IcpState {
            return state.icpData
        },
        isOffline (state: State): boolean {
            return state.isOffline
        },
        privacyPolicy (state: State): string {
            return state.privacyPolicy
        },
        termsOfService (state: State): string {
            return state.termsOfService
        },
        useAgreement (state: State): string {
            return state.useAgreement
        },
        websocketsConnected(state: State): boolean {
            return state.websocketsConnected
        }
    }
}
